import { useChatStore } from "@stores/chat.store";

import type { TConversation, TConversationDirection } from "@stores/chat-domain.types";
import { API_REQUEST_STATUS, USER_TAG } from "@/utils/constants";
import { getConversationType } from "@/utils/helpers";

import type {
  TWelcomeVariant,
  TConversationPanelSectionHookResult,
} from "./conversation-panel-section.types";

const isConversationDirection = (value: string | undefined): value is TConversationDirection =>
  value === "from" || value === "to";

/** Retorna flags de estado do painel de conversa (ativa, bloqueada, vazia, só spam). */
export const useConversationPanelSection = (): TConversationPanelSectionHookResult => {
  const conversationChatType = useChatStore((s) => s.conversation.chatType);
  const conversationEmpty = useChatStore((s) => s.conversation.empty);
  const conversationStatus = useChatStore((s) => s.conversation.status);
  const conversationActive = useChatStore((s) => s.conversation.active);
  const isMessagesLoaded = useChatStore((s) => s.messageList.fetched);
  const conversationFromSource = useChatStore(
    (s) => s.conversationFrom.source,
  );
  const conversationToSource = useChatStore((s) => s.conversationTo.source);
  const storeSelectedTabIndex = useChatStore((s) => s.tabs.conversation);

  const direction = isConversationDirection(conversationChatType) ? conversationChatType : "to";

  const noConversation =
    conversationEmpty[direction] &&
    conversationStatus[direction] === API_REQUEST_STATUS.ready;

  const selectedConversation = getConversationType(storeSelectedTabIndex);

  const storeSelectedConversationSource =
    selectedConversation === "from"
      ? conversationFromSource
      : conversationToSource;

  const isBlocked = conversationActive?.userTag === USER_TAG.Blocked;

  const hasOnlySpam =
    Array.isArray(storeSelectedConversationSource) &&
    storeSelectedConversationSource.length > 0 &&
    storeSelectedConversationSource.every(
      (conv: TConversation) => conv.userTag === USER_TAG.Spam,
    );

  const hasActiveConversation =
    !noConversation && !!conversationActive?.chatId;

  const shouldShowConversation = isMessagesLoaded || isBlocked;

  const resolveWelcomeVariant = (): TWelcomeVariant => {
    const isReady = conversationStatus[direction] === API_REQUEST_STATUS.ready;

    if (!isReady) return "loading";

    const showWithout =
      direction !== "from" &&
      (noConversation || hasOnlySpam);

    return showWithout ? "withoutConversation" : "withConversation";
  };

  const welcomeVariant = resolveWelcomeVariant();

  return {
    hasActiveConversation,
    shouldShowConversation,
    welcomeVariant,
  };
};
